import { Card, Button, Badge, Progress } from '@lib/ui';
import { StatCards, type StatCardData } from '@lib/blocks';
import { Timeline, TimelineItem } from '@lib/ui';

// Icons
const SourcesIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-6"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
    />
  </svg>
);

const PersonasIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-6"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
    />
  </svg>
);

const ContentIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-6"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456Z"
    />
  </svg>
);

const ChannelIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-6"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z"
    />
  </svg>
);

const stats: StatCardData[] = [
  {
    id: '1',
    title: 'Active Sources',
    value: 12,
    trend: 8,
    icon: <SourcesIcon />,
    color: 'primary',
  },
  { id: '2', title: 'Personas', value: 4, trend: 25, icon: <PersonasIcon />, color: 'secondary' },
  {
    id: '3',
    title: 'Content Generated',
    value: 47,
    trend: 12,
    icon: <ContentIcon />,
    color: 'success',
  },
  { id: '4', title: 'Channels', value: 6, trend: -5, icon: <ChannelIcon />, color: 'info' },
];

const recentActivity = [
  {
    id: 1,
    action: 'Generated LinkedIn post',
    persona: 'Thought Leader',
    source: 'Product Vision Doc',
    time: '2 min ago',
  },
  {
    id: 2,
    action: 'Added new source',
    persona: null,
    source: 'Q4 Market Analysis.pdf',
    time: '15 min ago',
  },
  {
    id: 3,
    action: 'Generated Twitter thread',
    persona: 'Tech Educator',
    source: 'AI Research Paper',
    time: '1 hour ago',
  },
  { id: 4, action: 'Updated persona', persona: 'Brand Voice', source: null, time: '3 hours ago' },
  {
    id: 5,
    action: 'Generated blog post',
    persona: 'Thought Leader',
    source: 'Industry Trends Report',
    time: 'Yesterday',
  },
];

const recentContent = [
  {
    id: 1,
    title: 'The Future of AI-Assisted Content Creation',
    channel: 'LinkedIn',
    status: 'published',
    persona: 'Thought Leader',
  },
  {
    id: 2,
    title: '5 Key Insights from Our Latest Research',
    channel: 'Twitter',
    status: 'draft',
    persona: 'Tech Educator',
  },
  {
    id: 3,
    title: 'Why Traditional Content Creation is Broken',
    channel: 'Blog',
    status: 'review',
    persona: 'Brand Voice',
  },
  {
    id: 4,
    title: 'Weekly Newsletter: AI & Creativity',
    channel: 'Email',
    status: 'scheduled',
    persona: 'Thought Leader',
  },
];

const personas = [
  {
    id: 1,
    name: 'Thought Leader',
    description: 'Strategic, authoritative, forward-thinking',
    contentCount: 23,
    color: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300',
  },
  {
    id: 2,
    name: 'Tech Educator',
    description: 'Clear, practical, approachable',
    contentCount: 15,
    color: 'bg-secondary-100 text-secondary-700 dark:bg-secondary-900/30 dark:text-secondary-300',
  },
  {
    id: 3,
    name: 'Brand Voice',
    description: 'Consistent, professional, engaging',
    contentCount: 9,
    color: 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-300',
  },
  {
    id: 4,
    name: 'Community Builder',
    description: 'Warm, inclusive, conversational',
    contentCount: 0,
    color: 'bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-300',
  },
];

const statusColors: Record<string, 'success' | 'warning' | 'info' | 'primary'> = {
  published: 'success',
  draft: 'warning',
  review: 'info',
  scheduled: 'primary',
};

export default function Dashboard() {
  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Dashboard</h1>
          <p className="mt-1 text-gray-500 dark:text-dark-300">
            Your content creation overview. One insight, many expressions.
          </p>
        </div>
        <Button>
          <ContentIcon />
          <span className="ml-2">Create Content</span>
        </Button>
      </div>

      {/* Stats */}
      <StatCards cards={stats} />

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Recent Content - Takes 2 columns */}
        <Card className="lg:col-span-2 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Content</h2>
            <Button variant="flat" className="text-sm">
              View All
            </Button>
          </div>
          <div className="space-y-4">
            {recentContent.map((content) => (
              <div
                key={content.id}
                className="flex items-center justify-between p-4 rounded-lg border border-gray-100 dark:border-dark-600 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {content.title}
                  </h3>
                  <div className="mt-1 flex items-center gap-2 text-xs text-gray-500 dark:text-dark-300">
                    <span>{content.channel}</span>
                    <span>•</span>
                    <span>{content.persona}</span>
                  </div>
                </div>
                <Badge
                  color={statusColors[content.status]}
                  variant="soft"
                  className="capitalize ml-4"
                >
                  {content.status}
                </Badge>
              </div>
            ))}
          </div>
        </Card>

        {/* Personas Card */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Personas</h2>
            <Button variant="flat" className="text-sm">
              Manage
            </Button>
          </div>
          <div className="space-y-3">
            {personas.map((persona) => (
              <div
                key={persona.id}
                className="p-3 rounded-lg border border-gray-100 dark:border-dark-600 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors cursor-pointer"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                      {persona.name}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-dark-300 mt-0.5">
                      {persona.description}
                    </p>
                  </div>
                  <Badge variant="soft" className={persona.color}>
                    {persona.contentCount}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Activity and Quick Actions */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recent Activity */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Recent Activity
          </h2>
          <Timeline variant="outlined">
            {recentActivity.map((activity) => (
              <TimelineItem key={activity.id} title={activity.action} time={activity.time}>
                <div className="flex flex-wrap gap-2 mt-1">
                  {activity.persona && (
                    <Badge variant="soft" color="secondary" className="text-xs">
                      {activity.persona}
                    </Badge>
                  )}
                  {activity.source && (
                    <Badge variant="soft" color="primary" className="text-xs">
                      {activity.source}
                    </Badge>
                  )}
                </div>
              </TimelineItem>
            ))}
          </Timeline>
        </Card>

        {/* Content Pipeline Overview */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Content Pipeline
          </h2>
          <div className="space-y-6">
            {/* Sources to Insights */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700 dark:text-dark-200">
                  Sources Processed
                </span>
                <span className="text-sm text-gray-500 dark:text-dark-300">12 / 15</span>
              </div>
              <Progress value={80} color="primary" />
            </div>

            {/* Insights Generated */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700 dark:text-dark-200">
                  Insights Extracted
                </span>
                <span className="text-sm text-gray-500 dark:text-dark-300">47 total</span>
              </div>
              <Progress value={65} color="secondary" />
            </div>

            {/* Content Transformations */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700 dark:text-dark-200">
                  Content Generated This Week
                </span>
                <span className="text-sm text-gray-500 dark:text-dark-300">23 pieces</span>
              </div>
              <Progress value={45} color="success" />
            </div>

            {/* Quick Stats */}
            <div className="pt-4 border-t border-gray-100 dark:border-dark-600">
              <p className="text-sm text-gray-500 dark:text-dark-300 mb-3">
                "Ideas and sources should be the scarce inputs—not time, tone-matching, or
                distribution-specific rewriting."
              </p>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-semibold text-primary-600 dark:text-primary-400">
                    3.2x
                  </p>
                  <p className="text-xs text-gray-500 dark:text-dark-300">Content Multiplier</p>
                </div>
                <div>
                  <p className="text-2xl font-semibold text-secondary-600 dark:text-secondary-400">
                    68%
                  </p>
                  <p className="text-xs text-gray-500 dark:text-dark-300">Time Saved</p>
                </div>
                <div>
                  <p className="text-2xl font-semibold text-success-600 dark:text-success-400">4</p>
                  <p className="text-xs text-gray-500 dark:text-dark-300">Active Personas</p>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
